import React, { useEffect, useState, forwardRef } from "react";
import InputField from "./inputField";
import ComboBox from "./combobox";

// Type for a country entry
interface Country {
  name: string;
  flag: string;
  code: string;
  dialCode: string;
}

interface PhoneInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  country?: string; // country code (e.g. 'ZA')
  onCountryChange?: (country: string) => void;
  placeholder?: string;
  disabled?: boolean;
  state?: "error" | "success";
  sizes?: "lg" | "md" | "sm" | "xs";
  variant?: "solid" | "outline";
  color?: "primary" | "secondary" | "white";
  rounded?: string;
  extra?: string;
  extraContainer?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      id,
      value,
      onChange,
      country,
      onCountryChange,
      placeholder = "Phone number",
      disabled = false,
      state,
      sizes = "lg",
      variant = "outline",
      color = "primary",
      rounded = "rounded-2xl",
      extra = "",
      extraContainer = "",
      onFocus,
    },
    ref
  ) => {
    const [countries, setCountries] = useState<Country[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>(country || "ZA");

    useEffect(() => {
      // Load countries.json from public
      fetch("/countries.json")
        .then((res) => res.json())
        .then((data) => setCountries(data))
        .catch(() => setCountries([]));
    }, []);

    useEffect(() => {
      if (country && country !== selectedCountry) {
        setSelectedCountry(country);
      }
    }, [country]);

    // Find the selected country object
    const countryObj = countries.find((c) => c.code === selectedCountry) || countries[0];

    // Compose the full phone value (with dial code)
    const handleInputChange = (val: string) => {
      // Remove any leading dial code if present
      let clean = val;
      if (countryObj && clean.startsWith(countryObj.dialCode)) {
        clean = clean.slice(countryObj.dialCode.length);
      }
      // Remove leading zeros/spaces
      clean = clean.replace(/^0+/, "").replace(/^\s+/, "");
      onChange(countryObj ? countryObj.dialCode + clean : clean);
    };

    // When the country changes, update the value to use the new dial code
    const handleCountryChange = (code: string) => {
      setSelectedCountry(code);
      if (onCountryChange) onCountryChange(code);
      // Optionally, update the value to use the new dial code
      const newCountry = countries.find((c) => c.code === code);
      if (newCountry) {
        // Remove old dial code if present
        let clean = value;
        if (countryObj && clean.startsWith(countryObj.dialCode)) {
          clean = clean.slice(countryObj.dialCode.length);
        }
        clean = clean.replace(/^0+/, "").replace(/^\s+/, "");
        onChange(newCountry.dialCode + clean);
      }
    };

    // Extract the number part (without dial code)
    let numberValue = value;
    if (countryObj && value && value.startsWith(countryObj.dialCode)) {
      numberValue = value.slice(countryObj.dialCode.length);
    }

    // Compute classes for the outer container to match InputField/ComboBox
    const colorClasses = {
      primary: {
        solid: `text-primary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-primary text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      secondary: {
        solid: `text-secondary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-secondary text-secondary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      white: {
        solid: `text-primary bg-white placeholder:text-gray-500`,
        outline: `border border-white text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
    };
    const sizeClasses = {
      lg: "h-12 px-6 text-txt-lg",
      md: "h-10 px-4 text-txt-md",
      sm: "h-8 px-3 text-txt-sm",
      xs: "h-6 px-2 text-txt-xs",
    };
    const stateClasses = {
      error: "outline-red-500 text-red-500 placeholder:text-red-500 border-red-500",
      success: "outline-green-500 text-green-500 placeholder:text-green-500 border-green-500",
    };
    const outerClass = `flex w-full items-center gap-0 ${rounded} ${sizeClasses[sizes]} ${colorClasses[color][variant]} ${state ? stateClasses[state] : "outline-none"} ${disabled ? "border-none bg-gray-50/50" : "focus:opacity-100"} ${extraContainer}`;

    return (
      <div className={outerClass}>
        <div className="flex items-center h-full">
          <ComboBox
            id={id + "-country"}
            value={selectedCountry}
            onChange={handleCountryChange}
            items={countries.map((c) => ({
              label: `${c.flag} ${c.dialCode} ${c.name}`,
              value: c.code,
            }))}
            labelKey="label"
            valueKey="value"
            placeholder="Country"
            sizes={sizes}
            rounded={"rounded-none"}
            filter
            variant={variant}
            color={color === "white" ? "primary" : color}
            extra={extra}
            clearIcon={false}
            renderSelectedValue={(selected: string) => {
              const item = countries.find((c) => c.code === selected);
              return item ? `${item.flag} ${item.dialCode}` : null;
            }}
          />
        </div>
        <div className="flex-1">
          <InputField
            ref={ref}
            id={id}
            value={numberValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            state={state}
            sizes={sizes}
            variant={variant}
            color={color}
            rounded={"rounded-none"}
            extra={extra}
            extraContainer="w-full"
            onFocus={onFocus}
          />
        </div>
      </div>
    );
  }
);

export default PhoneInput;
